/* eslint-disable no-unused-vars */
import { useEffect, useState } from 'react';
import {
    Box,
    Button,
    Flex,
    FormControl,
    FormLabel,
    Input,
    Switch,
    Text,
    Grid,
    GridItem,
    HStack,
} from '@chakra-ui/react';
import CustomSelect from 'components/CustomSelect';
import InputText from 'components/Input';
import { Backbutton } from 'components/LandingPageComponents/styles/HeaderStyles';
import { backbtn } from '../components/Icons';
import { useNavigate } from 'react-router-dom';
import ReminderModal from './ReminderModal';
import RepeatModal from './RepeatModal';
import { useCustomers, useGetSalesPersons } from 'hooks/useCustomers';
import SearchableSelect from 'components/SearchableSelect';
import useDebounce from 'hooks/useDebounce';
import useGetUserDetails from 'hooks/useGetUserDetail';
import { useMeetingMutations } from 'hooks/useMeeting';
import toast from 'react-hot-toast';

/**
 * CreateMeetingForm
 * Full-page form for creating a CRM meeting.
 * @example <CreateMeetingForm />
 */
export function CreateMeetingForm() {
    const navigate = useNavigate();
    const [searchTerm, setSearchTerm] = useState('');
    const debouncedValue = useDebounce(searchTerm, 700);
    // Placeholder data
    const user = useGetUserDetails();

    const hosts = [
        { label: 'Olivia Rhye', value: 'olivia' },
        { label: 'Ochije Nnani', value: 'ochije' },
    ];
    // Form state
    const [form, setForm] = useState({
        title: '',
        date: '',
        time: '',
        endDate: '',
        endTime: '',
        location: '',
        salesperson: `${
            user?.flattenedUser?.attributes?.first_name ?? user?.flattenedUser?.email ?? ''
        } ${user?.flattenedUser?.attributes?.last_name ?? ''}`,
        participants: '',
        host: '',
        relatedTo: '',
        reminder: false,
        repeat: false,
    });
    const [reminderData, setReminderData] = useState({ date: '', time: '', notify: '' });
    const [repeatData, setRepeatData] = useState({ frequency: '' });
    const [reminderModalOpen, setReminderModalOpen] = useState(false);
    const [repeatModalOpen, setRepeatModalOpen] = useState(false);
    const { salespersons, isLoading } = useGetSalesPersons();
    const { fetchCustomers, customers, isLoading: isLoadingCustomer } = useCustomers();
    const { mutate, isPending } = useMeetingMutations();

    function handleChange(e) {
        const { name, value, type, checked } = e.target;
        setForm((prev) => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value,
        }));
        if (name === 'reminder') {
            if (!form.reminder && checked) setReminderModalOpen(true);
            if (form.reminder && !checked) setReminderModalOpen(false);
        }
        if (name === 'repeat') {
            if (!form.repeat && checked) setRepeatModalOpen(true);
            if (form.repeat && !checked) setRepeatModalOpen(false);
        }
    }

    useEffect(() => {
        fetchCustomers(debouncedValue);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [debouncedValue]);
    function handleSelect(name, value) {
        setForm((prev) => ({ ...prev, [name]: value }));
    }
    function handleReminderChange(e) {
        const { name, value } = e.target;
        setReminderData((prev) => ({ ...prev, [name]: value }));
    }
    function handleSetReminder() {
        setReminderModalOpen(false);
    }
    function handleRepeatChange(e) {
        const { name, value } = e.target;
        setRepeatData((prev) => ({ ...prev, [name]: value }));
    }
    function handleSetRepeat() {
        setRepeatModalOpen(false);
    }
    async function handleSubmit(e) {
        e.preventDefault();
        const payload = {
            data: [
                {
                    All_day: !!form.endDate,
                    Participants: form?.participants.map((item) => ({
                        type: 'contact',
                        participant: item?.contact_id,
                    })),
                    Check_In_Status: 'PLANNED',
                    // "Recurring_Activity": {
                    // "RRULE": "FREQ=WEEKLY;INTERVAL=1;BYDAY=MO;DTSTART=2025-06-03;UNTIL=-1"
                    //},
                    Description: form.title,
                    Start_DateTime: `${form.date}T${form.time}:00+01:00`,
                    End_DateTime:
                        form.endDate && form.endTime
                            ? `${form.endDate}T${form.endTime}:00+01:00`
                            : `${form.date}T23:59:59+01:00`,
                    Event_Title: form.title,
                    Salesperson: form.salesperson,
                    $send_notification: !!reminderData?.date,
                },
            ],
        };
        console.log({ payload, form, reminderData, repeatData });

        const response = await mutate(payload);
        if (response) {
            navigate('/dashboard/crm?tab=tasks');
            toast.success('task created successfully');
        }
    }
    return (
        <Box as="form" onSubmit={handleSubmit} width={'100%'} mt={8} p={8}>
            <HStack justify="start" alignItems="center" display={'flex'}>
                <Backbutton onClick={() => navigate(-1)}>{backbtn}</Backbutton>
                <Text as="h1" fontSize="2xl" fontWeight="bold" mb={2} color="#1B3B5F">
                    Create New Meeting
                </Text>
            </HStack>
            <Text fontSize="sm" color="gray.500" mb={8}>
                Manage customer relationships from one place
            </Text>
            <Box borderWidth={1} borderColor="#EAECF0" borderRadius="lg" p={8}>
                <Text as="h2" fontWeight="bold" fontSize="lg" mb={6} color="#1B3B5F">
                    Meeting Information
                </Text>
                <Grid templateColumns={{ base: '1fr', lg: 'repeat(2, 1fr)' }} gap={6}>
                    <GridItem>
                        <FormControl mb={4} isRequired>
                            <FormLabel>Title</FormLabel>
                            <InputText
                                name="title"
                                value={form.title}
                                onChange={handleChange}
                                placeholder="Enter Title"
                                aria-label="Title"
                            />
                        </FormControl>
                    </GridItem>
                    <GridItem>
                        <FormControl mb={4}>
                            <FormLabel>Location</FormLabel>
                            <InputText
                                name="location"
                                value={form.location}
                                onChange={handleChange}
                                placeholder="Enter Location"
                                aria-label="Location"
                            />
                        </FormControl>
                    </GridItem>
                    <GridItem>
                        <FormControl mb={4} isRequired>
                            <FormLabel>Start Date</FormLabel>
                            <Input
                                type="date"
                                name="date"
                                value={form.date}
                                onChange={handleChange}
                                aria-label="Date"
                            />
                        </FormControl>
                    </GridItem>
                    <GridItem>
                        <FormControl mb={4} isRequired>
                            <FormLabel>Start Time</FormLabel>
                            <Input
                                type="time"
                                name="time"
                                value={form.time}
                                onChange={handleChange}
                                aria-label="Time"
                            />
                        </FormControl>
                    </GridItem>
                    <GridItem>
                        <FormControl mb={4}>
                            <FormLabel>End Date(Leave blank for all day meeting)</FormLabel>
                            <Input
                                type="date"
                                name="endDate"
                                value={form.endDate}
                                onChange={handleChange}
                                aria-label="end Date"
                            />
                        </FormControl>
                    </GridItem>
                    <GridItem>
                        <FormControl mb={4}>
                            <FormLabel>End Time (Leave blank for all day meeting)</FormLabel>
                            <Input
                                type="time"
                                name="endTime"
                                value={form.endTime}
                                onChange={handleChange}
                                aria-label="End Time"
                            />
                        </FormControl>
                    </GridItem>
                    <GridItem>
                        <FormControl mb={4} isRequired>
                            <FormLabel>Salesperson</FormLabel>

                            <InputText
                                name="salesperson"
                                value={form.salesperson}
                                aria-label="Salesperson"
                                isReadonly
                            />
                        </FormControl>
                    </GridItem>
                    <GridItem>
                        <FormControl mb={4} isRequired>
                            <FormLabel>Host</FormLabel>
                            <CustomSelect
                                name="host"
                                options={
                                    salespersons?.map((item) => ({
                                        ...item,
                                        label: `${item?.Name ?? ''} ${item?.Last_Name ?? ''}${
                                            item?.email ?? ''
                                        }`,
                                    })) ?? []
                                }
                                placeholder="Choose Host"
                                value={form.host}
                                onChange={(e) => handleSelect('host', e.target.value)}
                                aria-label="Host"
                            />
                        </FormControl>
                    </GridItem>
                    <GridItem>
                        <FormControl mb={4}>
                            <FormLabel>Participants</FormLabel>

                            <SearchableSelect
                                name="participants"
                                searchTerm={searchTerm}
                                setSearchTerm={setSearchTerm}
                                options={
                                    customers?.map((item) => ({
                                        ...item,
                                        contact_name: `${item?.contact_name} (${item?.email})`,
                                    })) ?? []
                                }
                                loading={isLoadingCustomer}
                                aria-label="Participants"
                                multiSelect
                                onSelect={(val) => {
                                    handleSelect('participants', val);
                                }}
                            />
                        </FormControl>
                    </GridItem>
                    <GridItem>
                        <FormControl mb={4}>
                            <FormLabel>Related To</FormLabel>
                            <InputText
                                name="relatedTo"
                                value={form.relatedTo}
                                onChange={handleChange}
                                placeholder="None"
                                aria-label="Related To"
                            />
                        </FormControl>
                    </GridItem>
                    <GridItem>
                        <FormControl display="flex" alignItems="center" mb={4}>
                            <FormLabel htmlFor="reminder" mb="0">
                                Set Reminder
                            </FormLabel>
                            <Switch
                                id="reminder"
                                name="reminder"
                                isChecked={form.reminder}
                                onChange={handleChange}
                                iconColor="#1b3b5f"
                                aria-label="Set Reminder"
                            />
                        </FormControl>
                    </GridItem>
                    <GridItem>
                        <FormControl display="flex" alignItems="center" mb={4}>
                            <FormLabel htmlFor="repeat" mb="0">
                                Set On Repeat
                            </FormLabel>
                            <Switch
                                id="repeat"
                                name="repeat"
                                isChecked={form.repeat}
                                onChange={handleChange}
                                iconColor="#1b3b5f"
                                aria-label="Set On Repeat"
                            />
                        </FormControl>
                    </GridItem>
                </Grid>
                <Flex mt={8} gap={4} justifyContent={{ base: 'center', md: 'flex-start' }}>
                    <Button
                        isLoading={isPending}
                        type="submit"
                        color="white"
                        bg="#1b3b5f"
                        px={8}
                        fontWeight="bold">
                        Create Meeting
                    </Button>
                    <Button
                        color="#1b3b5f"
                        bg="#B8C1D380"
                        variant="outline"
                        px={8}
                        fontWeight="bold"
                        onClick={() => {}}>
                        Cancel
                    </Button>
                </Flex>
            </Box>
            <ReminderModal
                isOpen={reminderModalOpen}
                onClose={() => setReminderModalOpen(false)}
                value={reminderData}
                onChange={handleReminderChange}
                onSet={handleSetReminder}
            />
            <RepeatModal
                isOpen={repeatModalOpen}
                onClose={() => setRepeatModalOpen(false)}
                value={repeatData}
                onChange={handleRepeatChange}
                onSet={handleSetRepeat}
            />
        </Box>
    );
}
